import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

type FormErrors<T> = {
    [K in keyof T]?: string | null;
};

type FormValidator<T> = (values: T) => FormErrors<T>;
type ValidationMode = 'onChange' | 'onBlur' | 'onSubmit';

interface UseFormOptions<T> {
    initialValues: T;
    validate?: FormValidator<T>;
    validationMode?: ValidationMode;
    enableReinitialize?: boolean;
    returnTouchedValues?: boolean;
}

export function useForm<T extends Record<string, any>>({
    initialValues,
    validate,
    validationMode = 'onChange',
    enableReinitialize = false,
    returnTouchedValues = false
}: UseFormOptions<T>) {
    const [values, setValues] = useState<T>(initialValues);
    const [errors, setErrors] = useState<FormErrors<T>>({});
    const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitCount, setSubmitCount] = useState(0);

    const initialRef = useRef(initialValues);

    // 🔁 Reset if `initialValues` changes and reinitialization is enabled
    useEffect(() => {
        if (
            enableReinitialize &&
            JSON.stringify(initialRef.current) !== JSON.stringify(initialValues)
        ) {
            setValues(initialValues);
            setErrors({});
            setTouched({});
            initialRef.current = initialValues;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableReinitialize, initialValues]);

    const dirty = useMemo(() => {
        const result: Partial<Record<keyof T, boolean>> = {};
        for (const key in values) {
            result[key] = values[key] !== initialRef.current[key];
        }
        return result;
    }, [values]);

    const isDirty = useMemo(() => Object.values(dirty).some(Boolean), [dirty]);
    const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

    const runValidation = useCallback(
        (nextValues: T) => {
            if (!validate) return {};
            const validationErrors = validate(nextValues);
            setErrors(validationErrors);
            return validationErrors;
        },
        [validate]
    );

    const validateField = useCallback(
        <K extends keyof T>(name: K) => {
            if (!validate) return null;
            const validationErrors = validate(values);
            const fieldError = validationErrors[name];
            setErrors(prev => ({ ...prev, [name]: fieldError }));
            return fieldError;
        },
        [validate, values]
    );

    const setFieldValue = useCallback(
        <K extends keyof T>(name: K, value: T[K]) => {
            setValues(prev => {
                const nextValues = { ...prev, [name]: value };

                if (validationMode === 'onChange') {
                    runValidation(nextValues);
                }

                return nextValues;
            });
            setTouched(prev => ({ ...prev, [name]: true }));
        },
        [validationMode, runValidation]
    );

    const setFieldError = useCallback(<K extends keyof T>(name: K, error: string | null) => {
        setErrors(prev => ({ ...prev, [name]: error }));
    }, []);

    const setFieldTouched = useCallback(<K extends keyof T>(name: K, touched: boolean = true) => {
        setTouched(prev => ({ ...prev, [name]: touched }));
    }, []);

    const handleChange = useCallback(
        <K extends keyof T>(name: K, value: T[K]) => {
            setFieldValue(name, value);
        },
        [setFieldValue]
    );

    const handleBlur = useCallback(
        <K extends keyof T>(name: K) => {
            setFieldTouched(name, true);

            if (validationMode === 'onBlur') {
                validateField(name);
            }
        },
        [validationMode, validateField, setFieldTouched]
    );

    const validateAll = useCallback((): boolean => {
        const validationErrors = runValidation(values);
        return Object.keys(validationErrors).length === 0;
    }, [runValidation, values]);

    const getTouchedValues = useCallback(() => {
        const result: Partial<Record<keyof T, any>> = {};
        for (const key in values) {
            if (touched[key as keyof T]) {
                result[key] = values[key as keyof T];
            }
        }
        return result;
    }, [touched, values]);

    const handleSubmit = useCallback(
        async (
            onValid: (values: T) => void | Promise<void>,
            onInvalid?: (errors: FormErrors<T>) => void
        ) => {
            setSubmitCount(prev => prev + 1);
            setIsSubmitting(true);

            // Mark all fields as touched when submitting
            const allTouched: Partial<Record<keyof T, boolean>> = {};
            for (const key in values) {
                allTouched[key] = true;
            }
            setTouched(allTouched);

            const valid = validateAll();
            if (valid) {
                try {
                    await onValid(returnTouchedValues ? (getTouchedValues() as T) : values);
                    // Update initialRef to current values after successful submission
                    // This ensures isDirty is reset to false after successful submission
                    initialRef.current = { ...values };
                    // Reset touched state after successful submission
                    // This ensures only newly touched fields are tracked in subsequent edits
                    setTouched({});
                } catch (error) {
                    console.error('Form submission error:', error);
                }
            } else {
                onInvalid?.(errors);
            }

            setIsSubmitting(false);
        },
        [validateAll, values, errors, returnTouchedValues, getTouchedValues]
    );

    const reset = useCallback((values?: Partial<T>) => {
        const newValues = { ...initialRef.current, ...values } as T;
        setValues(newValues);
        setErrors({});
        setTouched({});
        setIsSubmitting(false);
        setSubmitCount(0);
        // Update initialRef to the new values if provided
        if (values) {
            initialRef.current = newValues;
        }
    }, []);

    const resetTouched = useCallback(() => {
        setTouched({});
    }, []);

    const getFieldProps = useCallback(
        <K extends keyof T>(name: K) => ({
            name,
            value: values[name],
            onChange: (
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
            ) => {
                const target = e.target;
                let value: T[K];

                if (target.type === 'checkbox') {
                    value = (target as HTMLInputElement).checked as T[K];
                } else if (target.type === 'number') {
                    value = Number(target.value) as T[K];
                } else {
                    value = target.value as T[K];
                }

                handleChange(name, value);
            },
            onBlur: () => handleBlur(name)
        }),
        [values, handleChange, handleBlur]
    );

    const setFormValues = useCallback((values: Partial<T>) => {
        setValues(prev => ({ ...prev, ...values } as T));
        setTouched(prev => ({
            ...prev,
            ...Object.keys(values).reduce(
                (acc, key) => {
                    acc[key as keyof T] = true;
                    return acc;
                },
                {} as Partial<Record<keyof T, boolean>>
            )
        }));
    }, []);

    return {
        values,
        errors,
        touched,
        dirty,
        isDirty,
        isValid,
        isSubmitting,
        submitCount,
        handleChange,
        handleBlur,
        handleSubmit,
        validateAll,
        validateField,
        reset,
        resetTouched,
        getFieldProps,
        setFieldValue,
        setFieldError,
        setFieldTouched,
        setFormValues
    };
}
